import React from "react";

import { Skeleton, Typography } from "@mui/material";

import _ from "lodash";
import { formatMoneyString } from "src/utility/utilityFunctions";

import BudgetValue from "@features/budgets/BudgetValue";
import { useOrderSetBudgetSpend } from "@features/budgets/OrderSetBudgetSpendContext";
import { OrderVariant } from "@models/OrderVariant";
import { cn } from "@utils/tailwind";

import PaperTooltip from "../../ui/PaperTooltip";
import { useCurrentOrderSet } from "./data/CurrentOrderSetContext";

const LoadingValue = ({
  value,
  render = formatMoneyString,
}: {
  value: number | null;
  render?: (value: number) => React.ReactNode;
}) => {
  if (value === null) {
    return <Skeleton variant="text" width={100} />;
  }
  return render(value);
};

const OrderVariantBudget = ({
  orderVariant,
}: {
  orderVariant: OrderVariant;
}) => {
  const { budgets } = orderVariant;
  const { budgetSpends, isPending } = useOrderSetBudgetSpend();
  const { orderSetSpendPerBudget, isCoopOrderSet } = useCurrentOrderSet();

  if (budgets.length === 0 || isCoopOrderSet) {
    return null;
  }

  const budgetRemainingAfterOrder = (b: { id: string }) =>
    !budgetSpends?.[b.id]
      ? null
      : +budgetSpends[b.id].actualAvailableAmount -
        orderSetSpendPerBudget[b.id];

  const isOverBudget =
    budgetSpends && budgets.some((b) => budgetRemainingAfterOrder(b)! < 0);

  const multiBudgetBreakdown = (
    <>
      <h3 className="font-medium mb-3">Multi-budget</h3>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        {budgets.map((budget) => (
          <React.Fragment key={budget.id}>
            <div className="text-neutral-500 tracking-wide">
              {budget.subStateCode} x {budget.brandExternalId}
            </div>
            <div className="text-right">
              <LoadingValue
                value={budgetRemainingAfterOrder(budget)}
                render={(v) => <BudgetValue value={v} />}
              />
            </div>
          </React.Fragment>
        ))}
      </div>
    </>
  );

  return (
    <PaperTooltip
      title={budgets.length > 1 ? multiBudgetBreakdown : null}
      placement="top-end"
    >
      <div className="text-xs absolute z-10 top-0.5 left-3 right-3 grid grid-cols-[1fr_auto] gap-2 items-baseline text-neutral-400 hover:text-neutral-800">
        {budgets.length === 1 ? (
          <Typography noWrap className="text-xs">
            {budgets[0].subStateCode} x {budgets[0].brandExternalId}
          </Typography>
        ) : (
          <div>Multi</div>
        )}
        {isPending ? (
          <Skeleton variant="text" width={100} />
        ) : (
          <div
            className={cn(isOverBudget && "text-red-600 hover:text-red-900")}
          >
            <LoadingValue
              value={_.sumBy(budgets, (b) => budgetRemainingAfterOrder(b) ?? 0)}
            />
          </div>
        )}
      </div>
    </PaperTooltip>
  );
};

export default OrderVariantBudget;
